import { CepField } from '@components/Fields/CepField';
import SettingsPhoneIcon from '@material-ui/icons/SettingsPhone';
import { ComboField } from '@components/Fields/ComboField';
import { Fieldset } from '@components/Fields/Fieldset';
import { GridItem } from '@components/GridItem';
import { HorizontalGrid } from '@components/HorizontalGrid';
import { StringField } from '@components/Fields/StringField';
import { TelefoneField } from '@components/Fields/TelefoneField';
import { VerticalGrid } from '@components/VerticalGrid';
import { useComboBoolean } from '@hooks/useComboBoolean';
import { IForm } from '@hooks/useForm';
import useUf from '@hooks/useUf';
import { Grid, MenuItem } from '@material-ui/core';
import AssignmentIndIcon from '@material-ui/icons/AssignmentInd';
import CommentIcon from '@material-ui/icons/Comment';
import ImportContactsIcon from '@material-ui/icons/ImportContacts';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import RecordVoiceOverIcon from '@material-ui/icons/RecordVoiceOver';
import { ICliente } from '@model/Cliente';
import { EnumClienteTipo } from '@model/ClienteTipo';
import { IEndereco } from '@model/Endereco';
import { IComboRemetenteWhatsapp } from '@model/RemetenteWhatsapp';
import React from 'react';
import { DateField } from '@components/Fields/DateField';


export type ClienteFormularioProps = {
    remetenteWhatsappCombo: IComboRemetenteWhatsapp
    form: IForm<Partial<ICliente>>
}

export function ClienteFormulario ({
    form, 
    remetenteWhatsappCombo
}: ClienteFormularioProps) {
    
    const spcCombo = useComboBoolean("0");
    const { fields } = form;

    const uf = useUf();

    const inputNumeroRef = React.useRef<any>();

    const handleCepSearch = React.useCallback((endereco: Partial<IEndereco>) => {
        fields.enderecoLogradouro.setValue(endereco.endereco);
        fields.enderecoBairro.setValue(endereco.bairro);
        fields.enderecoCidade.setValue(endereco.cidade);
        fields.enderecoUf.setValue(endereco.uf);

        setTimeout(() => {
            inputNumeroRef.current.focus();
        }, 50);
    }, [fields.enderecoBairro, fields.enderecoCidade, fields.enderecoLogradouro, fields.enderecoUf]);

    return (
        <VerticalGrid>
            <Fieldset
                icon={<AssignmentIndIcon />}
                title="Identificação"
            >
                <HorizontalGrid spacing={2}>
                    <GridItem xs={4}>
                        <StringField
                            field={fields.nome}
                            label="Nome Completo"
                            required
                        />
                    </GridItem>

                    {
                        fields.clienteTipoId.value === EnumClienteTipo.PessoaFisica && (
                            <React.Fragment>
                                <GridItem xs={2}>
                                    <StringField
                                        field={fields.rg}
                                        label="Identidade"
                                        required
                                    />
                                </GridItem>

                                <GridItem xs={2}>
                                    <DateField
                                        field={fields.dataNascimento}
                                        label="Data Nascimento"
                                    />
                                </GridItem>

                                <GridItem xs={2}>
                                    <StringField
                                        field={fields.cartaoSus}
                                        label="Cartão SUS"
                                    />
                                </GridItem>
                            </React.Fragment>
                        )
                    }

                    <GridItem xs={2}>
                        <ComboField
                            field={fields.spc}
                            label="SPC"
                            required
                        >
                            {spcCombo.createOptions()}
                        </ComboField>
                    </GridItem>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                icon={<ImportContactsIcon />}
                title="Contato"
            >
                <HorizontalGrid spacing={2}>
                    <GridItem xs={3}>
                        <StringField 
                            field={fields.localTrabalho}
                            label="Local de Trabalho"
                        />
                    </GridItem>

                    <GridItem xs={3}>
                        <StringField 
                            field={fields.cargo}
                            label="Cargo"
                        />
                    </GridItem>

                    <GridItem xs={2}>
                        <StringField 
                            field={fields.email}
                            label="E-mail"
                        />
                    </GridItem>

                    <GridItem xs={2}>
                        <TelefoneField 
                            field={fields.celular}
                            label="Celular"
                            maskType="cel"
                            helperText="Usado para receber Whatsapp"
                        />
                    </GridItem>

                    <GridItem xs={2}>
                        <StringField
                            field={fields.whatsappEnvio}
                            label="Remetente Whatsapp"
                            helperText="Usado para enviar Whatsapp"
                            fullWidth
                            select
                            required
                        >
                            <MenuItem key="0" value="0">Nenhum</MenuItem>
                            {
                                remetenteWhatsappCombo.createOptionsWithNumero()
                            }
                        </StringField>
                    </GridItem>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset 
                icon={<SettingsPhoneIcon />}
                title="Telefones Alternativos"
            >
                <HorizontalGrid>
                    <GridItem xs={3}>
                        <TelefoneField 
                            field={fields.telefoneAlternativo}
                            label="Telefone Alternativo"
                            maskType="both"
                            fullWidth
                        />
                    </GridItem>

                    <GridItem xs={3}>
                        <TelefoneField 
                            field={fields.telefoneAlternativo2}
                            label="Telefone Alternativo 2"
                            maskType="both"
                            fullWidth
                        />
                    </GridItem>

                    <GridItem xs={3}>
                        <TelefoneField 
                            field={fields.telefoneAlternativo3}
                            label="Telefone Alternativo 3"
                            maskType="both"
                            fullWidth
                        />
                    </GridItem>

                    <GridItem xs={3}>
                        <TelefoneField 
                            field={fields.telefoneAlternativo4}
                            label="Telefone Alternativo 4"
                            maskType="both"
                            fullWidth
                        />
                    </GridItem>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                icon={<LocationOnIcon />}
                title="Endereço"
            >
                <HorizontalGrid spacing={2}>
                    <Grid item xs={3}>
                        <CepField 
                            field={fields.enderecoCep}
                            label="CEP"
                            required
                            onCepSearch={handleCepSearch}
                        />
                    </Grid>

                    <Grid item xs={5}>
                        <StringField 
                            field={fields.enderecoLogradouro}
                            label="Logradouro"
                            required
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <StringField
                            field={fields.enderecoNumero}
                            label="Nº"
                            inputRef={inputNumeroRef}
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <StringField 
                            field={fields.enderecoComplemento}
                            label="Compl."
                        />
                    </Grid>

                    <Grid item xs={5}>
                        <StringField 
                            field={fields.enderecoBairro}
                            label="Bairro"
                            required
                        />
                    </Grid>

                    <Grid item xs={5}>
                        <StringField 
                            field={fields.enderecoCidade}
                            label="Cidade"
                            required
                        />
                    </Grid>

                    <Grid item xs={2}>
                        <ComboField 
                            field={fields.enderecoUf}
                            label="UF"
                            required
                        >
                            {uf.createOptions()}
                        </ComboField>
                    </Grid>

                    <Grid item xs={12}>
                        <StringField
                            field={fields.enderecoReferencia}
                            label="Ponto de Referência"
                            fullWidth
                        />
                    </Grid>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                icon={<RecordVoiceOverIcon />}
                title="Indicação"
            >
                <HorizontalGrid spacing={2}>
                    <Grid item xs={4}>
                        <StringField 
                            field={fields.indicadoPor}
                            label="Indicado Por"
                        />
                    </Grid>

                    <Grid item xs={4}>
                        <TelefoneField 
                            field={fields.telefoneIndicacao}
                            label="Telefone Indicação"
                            maskType="both"
                        />
                    </Grid>
                </HorizontalGrid>
            </Fieldset>

            <Fieldset
                icon={<CommentIcon />}
                title="Observação"
            >
                <HorizontalGrid spacing={2}>
                    <Grid item xs={12}>
                        <StringField
                            multiline
                            field={fields.observacao}
                        />
                    </Grid>
                </HorizontalGrid>
            </Fieldset>
        </VerticalGrid>
    )
}